<template>
  <div class="page-admin-evaluation-grid">
    <BaseLoadingBar is-small v-if="pending" />
    <div class="page-admin-evaluation-grid__empty-state" v-if="!selectedTemplate">
      <KingdomDogSvg class="page-admin-evaluation-grid__empty-state-image" />
      <div class="page-admin-evaluation-grid__empty-state-message">
        {{ $t('page.admin-evaluation-grid.no-template-message') }}
      </div>
      <BaseButton isSquare @click.native="openAddTemplate" class="page-admin-evaluation-grid__add">
        <BaseIcon name="plus" />
        {{ $t('page.admin-evaluation-grid.add-template') }}
      </BaseButton>
    </div>
    <div v-else class="page-admin-evaluation-grid__body">
      <div class="page-admin-evaluation-grid__actions">
        <BaseSelect
          :label="$t('page.admin-evaluation-grid.template')"
          :value="selectedTemplate"
          :items="evaluationGridTemplates"
          item-key="id"
          item-text="name"
          item-value="id"
          :name="selectedTemplate.name"
          @change="onTemplateSelect"
        />
        <div class="page-admin-evaluation-grid__template-actions">
          <div v-if="selectedTemplate">
            <BaseTag tone="success" preset="soft" v-if="selectedTemplate.isPrimary">
              {{ $t('page.admin-evaluation-grid.default') }}
            </BaseTag>
            <BaseRoundButton
              v-else-if="!isPhone"
              :size="32"
              icon="flag"
              :tooltip-text="$t('page.admin-evaluation-grid.make-default')"
              is-transparent
              @click.native="makeGridTemplateDefault"
            />
          </div>
          <!-- Phones: the template's actions in its ⋯, as Profil d'entreprise's (four buttons ran past
               a narrow phone's margin, beside a second "+"). -->
          <BaseMoreActions
            v-if="isPhone"
            :title="selectedTemplate.name"
            :actions="templateActions()"
            @select="onTemplateAction"
          />
          <template v-else>
            <BaseRoundButton
              :size="32"
              icon="plus"
              :tooltip-text="$t('actions.add')"
              is-transparent
              @click.native="openAddTemplate"
            />
            <BaseRoundButton
              :size="32"
              icon="edit"
              :tooltip-text="$t('actions.edit')"
              is-transparent
              @click.native="openEditTemplate"
            />
            <BaseRoundButton
              :size="32"
              icon="trash-2"
              :tooltip-text="$t('actions.delete')"
              is-transparent
              @click.native="openDeleteTemplate"
              class="page-admin-evaluation-grid__delete-icon"
            />
          </template>
        </div>
      </div>
      <!-- Phones: its main action, as the "+" of the settings header. -->
      <PageMainAction :label="$t('page.admin-evaluation-grid.add-field')" @click="openAddField" />
      <div v-if="!isPhone" class="page-admin-evaluation-grid__table-header-actions">
        <div class="page-admin__count" v-if="selectedTemplate && selectedTemplate.fields">
          <span class="number-monospace">{{ formatLocalizedNumber(selectedTemplate.fields.length) }}</span>
          {{ ' ' + $tc('page.admin-evaluation-grid.field', selectedTemplate.fields.length) }}
        </div>
        <div class="page-admin__actions">
          <BaseRefreshButton :size="40" :pending="pending" @refresh="onRefresh" />
          <BaseButton @click.native="openAddField" has-icon-left>
            <BaseIcon name="plus" />
            {{ $t('page.admin-evaluation-grid.add-field') }}
          </BaseButton>
        </div>
      </div>
      <div
        class="page-admin-evaluation-grid__table-wrapper"
        v-if="selectedTemplate.fields && selectedTemplate.fields.length !== 0"
      >
        <TableNormal
          v-sortable
          :headers="headers"
          :items="sortedFields"
          :items-per-page="-1"
          item-key="id"
          :loading="pending"
          hide-default-footer
          :mobile-breakpoint="620"
          @sorted="onTableSorted"
        >
          <!-- Phones: the table draws the count line above its rows (docs/page-layout.md). -->
          <template v-if="selectedTemplate && selectedTemplate.fields" #phone-count>
            <span class="number-monospace">{{ formatLocalizedNumber(selectedTemplate.fields.length) }}</span>
            {{ ' ' + $tc('page.admin-evaluation-grid.field', selectedTemplate.fields.length) }}
          </template>
          <template #phone-actions>
            <BaseRefreshButton :pending="pending" @refresh="onRefresh" />
          </template>
          <template v-slot:item.sort>
            <div class="drag-handle">
              <BaseIcon name="menu" class="page-admin-evaluation-grid__dnd-icon" />
            </div>
          </template>

          <template v-slot:item.priority="{ item }">
            <div>
              <BaseRoundButton
                :size="40"
                :icon="getPinIcon(item)"
                :tooltip-text="getPinTooltip(item)"
                is-transparent
                @click.native="executeToggleGridFieldPriority(item)"
                @mouseenter.native="pinHovered = item.id"
                @mouseleave.native="pinHovered = null"
              />
            </div>
          </template>

          <template v-slot:item.name="{ item }">
            <div class="page-admin-evaluation-grid__field-name">
              <BaseIcon
                name="ai"
                class="ai-icon"
                v-if="item.useInExtraction"
                v-tooltip="$t('page.admin-evaluation-grid.field-ai-extraction-tooltip')"
              />
              {{ item.name }}
            </div>
            <div v-if="!isPhone" class="page-admin-evaluation-grid__field-description">{{ item.description }}</div>
          </template>
          <template v-slot:item.choices="{ item }">
            <template v-if="item.type === 'SELECT'">
              <div class="page-admin-evaluation-grid__item-choices">
                <BaseChip
                  v-for="(choice, choiceIdx) in item.choices"
                  :key="choiceIdx"
                  size="sm"
                  tone="neutral"
                  preset="soft"
                  :interactive="false"
                >
                  {{ choice }}
                </BaseChip>
              </div>
            </template>
            <span v-else-if="item.type === 'IMAGE'">{{ $t('page.admin-evaluation-grid.image') }}</span>
            <span v-else-if="item.type === 'NUMBER'">{{ $t('page.admin-evaluation-grid.number') }}</span>
            <span v-else>{{ $t('page.admin-evaluation-grid.free-text') }}</span>
          </template>
          <template v-slot:item.actions="{ item }">
            <BaseMoreActions
              v-if="isPhone"
              :title="item.name"
              :actions="rowActions(item)"
              @select="onRowAction($event, item)"
            />
            <v-menu v-else>
              <template v-slot:activator="{ on, attrs }">
                <div v-bind="attrs" v-on="on">
                  <BaseIcon name="more-horizontal" class="page-admin-evaluation-grid__more" />
                </div>
              </template>
              <div class="page-admin-evaluation-grid__actions-menu">
                <ul>
                  <li class="edit" @click="openEditField(item)">
                    <BaseIcon :size="44" name="edit" />
                    <span>{{ $t('actions.edit') }}</span>
                  </li>
                  <li class="delete" @click="openDeleteField(item)">
                    <BaseIcon :size="44" name="trash-2" />
                    <span>{{ $t('actions.delete') }}</span>
                  </li>
                </ul>
              </div>
            </v-menu>
          </template>
        </TableNormal>
        <v-overlay :value="isUpdatingOrders" absolute opacity="0.5" color="#ffffff" z-index="1">
          <BaseCircularLoadingBar is-small />
        </v-overlay>
      </div>
      <div
        class="page-admin-evaluation-grid__empty-state"
        v-if="selectedTemplate && selectedTemplate.fields && selectedTemplate.fields.length === 0"
      >
        <KingdomDogSvg class="page-admin-evaluation-grid__empty-state-image" />
        <div class="page-admin-evaluation-grid__empty-state-message">
          {{ $t('page.admin-evaluation-grid.no-fields-message') }}
        </div>
        <BaseButton isSquare @click.native="openAddField" class="page-admin-evaluation-grid__add">
          <BaseIcon name="plus" />
          {{ $t('page.admin-evaluation-grid.add-field') }}
        </BaseButton>
      </div>
    </div>

    <DeleteDialog
      v-if="fieldToDelete"
      ref="deleteFieldDialog"
      @execute="executeDeleteGridField"
      :buttonText="$t('actions.delete')"
    >
      <template slot="content">
        <p>{{ $t('page.admin-evaluation-grid.delete-field') }}</p>
        <p class="page-admin-evaluation-grid__field-to-delete">{{ fieldToDelete.name }}</p>
      </template>
    </DeleteDialog>
    <DeleteDialog
      v-if="selectedTemplate"
      ref="deleteTemplateDialog"
      @execute="executeDeleteGridTemplate"
      :buttonText="$t('actions.delete')"
    >
      <template slot="content">
        <p>{{ $t('page.admin-evaluation-grid.delete-template') }}</p>
        <p class="page-admin-evaluation-grid__field-to-delete">{{ selectedTemplate.name }}</p>
      </template>
    </DeleteDialog>

    <AddEvaluationGridFieldDialog
      ref="editEvalGridDialog"
      v-if="selectedTemplate && fieldToEdit"
      :evaluation-grid-template-field="fieldToEdit"
      :evaluation-grid-fields="selectedTemplate.fields"
      :buttonText="$t('actions.save')"
      @submit="executeEditGridField($event)"
    />
    <AddEvaluationGridFieldDialog
      ref="addEvalFieldDialog"
      :evaluation-grid-fields="selectedTemplate ? selectedTemplate.fields : []"
      :buttonText="$t('actions.save')"
      @submit="executeAddGridField($event)"
    />

    <AddEvaluationGridTemplate ref="addEvaluationTemplate" @action-succeeded="onRefresh" />
    <EditEvaluationGridTemplateDialog
      ref="editEvaluationTemplate"
      :templateId="templateToEdit.id"
      :templateName="templateToEdit.name"
      :template-is-primary="templateToEdit.isPrimary"
    />
    <BaseDialog centered contentClass="before-delete-dialog" ref="beforeDeleteDialog" :show-actions="true">
      <template slot="title">{{ $t('page.admin-evaluation-grid.before-delete-dialog.title') }}</template>
      <template slot="content">
        <div class="before-delete-dialog">
          <div>
            <div class="option-title">{{ $t('page.admin-evaluation-grid.before-delete-dialog.description') }}</div>
            <BaseSelect
              class="option-dropdown"
              name="evaluationGridTemplate"
              :items="evaluationGridTemplates"
              :value="selectedTemplateIdBeforeDelete"
              item-text="name"
              item-value="id"
              :placeholder="$t('llm-extraction-generate-dialog.model-choice')"
              @change="onBeforeDeleteDialogChange"
            />
          </div>

          <div class="before-delete-dialog__actions">
            <FormButton is-outlined @click.native="onBeforeDeleteDialogClose">{{ $t('actions.cancel') }}</FormButton>
            <FormButton
              @click.native="onBeforeDeleteDialogSubmit"
              type="submit"
              :disabled="isBeforeDeleteDialogFormInvalid"
            >
              <span>{{ $t('page.admin-evaluation-grid.before-delete-dialog.submit') }}</span>
            </FormButton>
          </div>
        </div>
      </template>
    </BaseDialog>
  </div>
</template>

<script>
import { formatLocalizedNumber } from '@/util/number-util';
import KingdomDogSvg from '../../assets/images/kingdom-dog.svg';
import AddEvaluationGridFieldDialog from '@/components/page-pdf-analysis/AddEvaluationGridFieldDialog.vue';
import BaseIcon from '@/components/base/BaseIcon.vue';
import BaseSelect from '@/components/base/BaseSelect.vue';
import BaseButton from '@/components/base/BaseButton.vue';
import BaseChip from '@/components/base/BaseChip.vue';
import BaseTag from '@/components/base/BaseTag.vue';
import BaseLoadingBar from '@/components/base/BaseLoadingBar.vue';
import DeleteDialog from '@/components/page-admin-keywords/DeleteDialog.vue';
import TableNormal from '@/components/table/TableNormal.vue';
import BaseRefreshButton from '@/components/base/BaseRefreshButton.vue';
import { mapActions, mapGetters } from 'vuex';
import AddEvaluationGridTemplate from '@/components/page-admin-evaluation-grid-template/AddEvaluationGridTemplateDialog.vue';
import EditEvaluationGridTemplateDialog from '@/components/page-admin-evaluation-grid-template/EditEvaluationGridTemplateDialog.vue';
import BaseCircularLoadingBar from '@/components/base/BaseCircularLoadingBar.vue';
import BaseRoundButton from '@/components/base/BaseRoundButton.vue';
import BaseDialog from '@/components/base/BaseDialog.vue';
import FormButton from '@/components/form/FormButton.vue';
import { sortEvaluationGridFields } from '@/util/evaluation-grid-util';
import PageMainAction from '@/components/layout/PageMainAction.vue';
import { viewport } from '@/util/viewport';
import BaseMoreActions from '@/components/base/BaseMoreActions.vue';

export default {
  name: 'PageAdminEvaluationGridTemplate',
  components: {
    BaseMoreActions,
    BaseRefreshButton,
    PageMainAction,
    FormButton,
    BaseDialog,
    KingdomDogSvg,
    BaseRoundButton,
    AddEvaluationGridFieldDialog,
    BaseIcon,
    BaseSelect,
    DeleteDialog,
    BaseButton,
    BaseLoadingBar,
    BaseChip,
    BaseTag,
    AddEvaluationGridTemplate,
    EditEvaluationGridTemplateDialog,
    TableNormal,
    BaseCircularLoadingBar,
  },
  data() {
    return {
      isUpdatingOrders: false,
      selectedTemplate: null,
      fieldToDelete: null,
      fieldToEdit: null,
      templateToEdit: {},
      selectedTemplateIdBeforeDelete: null,
      pinHovered: false,
    };
  },
  computed: {
    isPhone() {
      return viewport.size === 'phone';
    },
    // Phones: the description is the line under the name, and the pin the row's button up front,
    // beside its ⋯.
    headers() {
      return [
        { text: '', value: 'sort', phone: 'hidden', width: '50px' },
        { text: '', value: 'priority', phone: 'actions', width: '50px' },
        { text: this.$t('evaluation-grid.field'), value: 'name' },
        ...(this.isPhone ? [{ text: '', value: 'description', phone: 'subtitle' }] : []),
        { text: this.$t('evaluation-grid.management.field-type'), value: 'choices' },
        { text: '', value: 'actions', sortable: false, width: '70px' },
      ];
    },
    ...mapGetters('evaluationGridTemplates', ['evaluationGridTemplates', 'pending', 'error']),
    isBeforeDeleteDialogFormInvalid() {
      return !this.selectedTemplateIdBeforeDelete;
    },
    sortedFields() {
      if (!this.selectedTemplate || !this.selectedTemplate.fields) return [];
      return sortEvaluationGridFields(this.selectedTemplate).fields;
    },
  },
  methods: {
    // The row's ⋯ on phones, a sheet (BaseMoreActions); the desktop's menu lists the same.
    rowActions(item) {
      return [
        { key: 'edit', label: this.$t('actions.edit'), icon: 'edit-2' },
        { key: 'delete', label: this.$t('actions.delete'), icon: 'trash-2', danger: true },
      ];
    },
    onRowAction(key, item) {
      if (key === 'edit') this.openEditField(item);
      else this.openDeleteField(item);
    },
    // The template's ⋯ on phones: the desktop's buttons beside it, the default one's flag left out.
    templateActions() {
      return [
        ...(this.selectedTemplate && !this.selectedTemplate.isPrimary
          ? [{ key: 'default', label: this.$t('page.admin-evaluation-grid.set-default'), icon: 'flag' }]
          : []),
        { key: 'add', label: this.$t('page.admin-evaluation-grid.add-template'), icon: 'plus' },
        { key: 'edit', label: this.$t('actions.edit'), icon: 'edit-2' },
        { key: 'delete', label: this.$t('actions.delete'), icon: 'trash-2', danger: true },
      ];
    },
    onTemplateAction(key) {
      if (key === 'default') this.makeGridTemplateDefault();
      else if (key === 'add') this.openAddTemplate();
      else if (key === 'edit') this.openEditTemplate();
      else if (key === 'delete') this.openDeleteTemplate();
    },
    formatLocalizedNumber,
    ...mapActions('evaluationGridTemplateFields', [
      'addEvaluationGridTemplateField',
      'editEvaluationGridTemplateField',
      'deleteEvaluationGridTemplateField',
    ]),
    ...mapActions('evaluationGridTemplates', [
      'getEvaluationGridTemplates',
      'editEvaluationGridTemplate',
      'deleteEvaluationGridTemplate',
    ]),
    openAddField() {
      this.$refs.addEvalFieldDialog.openDialog();
    },
    openEditField(field) {
      this.fieldToEdit = field;
      this.$nextTick(() => {
        this.$refs.editEvalGridDialog.openDialog();
      });
    },
    openDeleteField(field) {
      this.fieldToDelete = field;
      this.$nextTick(() => {
        this.$refs.deleteFieldDialog.openDialog();
      });
    },
    openAddTemplate() {
      this.$refs.addEvaluationTemplate.openDialog();
    },
    openEditTemplate() {
      if (this.selectedTemplate) {
        this.templateToEdit = {
          id: this.selectedTemplate.id,
          name: this.selectedTemplate.name,
          isPrimary: this.selectedTemplate.isPrimary,
        };
        this.$refs.editEvaluationTemplate.openDialog();
      }
    },
    openDeleteTemplate() {
      if (this.selectedTemplate.isPrimary) {
        this.$refs.beforeDeleteDialog.openModal();
      } else {
        this.$refs.deleteTemplateDialog.openDialog();
      }
    },
    onBeforeDeleteDialogClose() {
      this.selectedTemplateIdBeforeDelete = null;
      this.$refs.beforeDeleteDialog.isModalOpen = false;
    },
    onBeforeDeleteDialogSubmit() {
      if (!this.selectedTemplateIdBeforeDelete) {
        return;
      }

      const templateToMakePrimary = this.evaluationGridTemplates.find(
        (template) => template.id === this.selectedTemplateIdBeforeDelete,
      );
      if (!templateToMakePrimary) {
        return;
      }

      this.editEvaluationGridTemplate({
        templateId: templateToMakePrimary.id,
        templateData: { name: templateToMakePrimary.name, isPrimary: true },
      })
        .then(() => {
          this.onRefresh();
          this.$toast.success(this.$t('page.admin-evaluation-grid.toast.make-default-success'));
          this.onBeforeDeleteDialogClose();
          this.$refs.deleteTemplateDialog.openDialog();
        })
        .catch((error) => {
          this.$toast.error(error);
        });
    },
    onBeforeDeleteDialogChange(value) {
      this.selectedTemplateIdBeforeDelete = value;
    },
    executeAddGridField(newFieldData) {
      newFieldData.order = this.selectedTemplate.fields.length + 1;

      const templateId = this.selectedTemplate.id;
      this.addEvaluationGridTemplateField({ templateId, fieldData: newFieldData })
        .then(() => {
          this.$toast.success(this.$t('page.admin-evaluation-grid.toast.add-field-success'));
          this.onRefresh();
          this.$refs.addEvalFieldDialog.closeDialog();
        })
        .catch((error) => {
          this.$toast.error(error);
        });
    },
    executeEditGridField(newFieldData) {
      const templateId = this.selectedTemplate.id;
      this.editEvaluationGridTemplateField({
        templateId,
        fieldId: newFieldData.id,
        fieldData: newFieldData,
      })
        .then(() => {
          this.$toast.success(this.$t('page.admin-evaluation-grid.toast.update-field-success'));
          this.onRefresh();
          this.$refs.addEvalFieldDialog.closeDialog();
        })
        .catch((error) => {
          this.$toast.error(error.message || this.$t('toast.error'));
        });
    },
    executeDeleteGridField() {
      if (this.fieldToDelete !== null) {
        const templateId = this.selectedTemplate.id;
        this.deleteEvaluationGridTemplateField({ templateId, fieldId: this.fieldToDelete.id })
          .then(() => {
            this.onRefresh();
            this.$toast.success(this.$t('page.admin-evaluation-grid.toast.delete-field-success'));
            this.fieldToDelete = null;
          })
          .catch((error) => {
            this.$toast.error(error);
          });
      }
    },
    async executeToggleGridFieldPriority(field) {
      const newPriority = field.priority === 'LOW' ? 'HIGH' : 'LOW';
      const fields = this.selectedTemplate.fields || [];
      const maxOrder = fields.length ? Math.max(...fields.map((f) => f.order || 0)) : 0;
      const updatedField = {
        ...field,
        priority: newPriority,
        order: newPriority === 'HIGH' ? 1 : maxOrder + 1,
      };

      this.editEvaluationGridTemplateField({
        templateId: this.selectedTemplate.id,
        fieldId: updatedField.id,
        fieldData: updatedField,
      })
        .then(() => {
          if (this.error) {
            this.$toast.error(this.error);
            return;
          }
          if (newPriority === 'HIGH') {
            this.$toast.success(this.$t('page.admin-evaluation-grid.toast.pin-field-success'));
          } else {
            this.$toast.success(this.$t('page.admin-evaluation-grid.toast.unpin-field-success'));
          }
          this.onTemplateSelect(this.selectedTemplate.id);
        })
        .catch((error) => {
          this.$toast.error(error);
        });
    },
    executeDeleteGridTemplate() {
      if (this.selectedTemplate) {
        const templateId = this.selectedTemplate.id;
        this.deleteEvaluationGridTemplate(templateId)
          .then(() => {
            this.onRefresh();
            this.$toast.success(this.$t('page.admin-evaluation-grid.toast.delete-template-success'));
          })
          .catch((error) => {
            this.$toast.error(error);
          });
      }
    },
    makeGridTemplateDefault() {
      if (!this.selectedTemplate) {
        return;
      }
      this.editEvaluationGridTemplate({
        templateId: this.selectedTemplate.id,
        templateData: { name: this.selectedTemplate.name, isPrimary: true },
      })
        .then(() => {
          this.onRefresh();
          this.$toast.success(this.$t('page.admin-evaluation-grid.toast.make-default-success'));
        })
        .catch((error) => {
          this.$toast.error(error);
        });
    },
    onTemplateSelect(templateId) {
      this.selectedTemplate = this.evaluationGridTemplates.find((template) => template.id === templateId);
    },
    async onTableSorted(event) {
      let { oldIndex, newIndex } = event;
      const sorted = [...this.sortedFields];
      const movedItem = sorted.splice(oldIndex, 1)[0];

      const highCount = this.sortedFields.filter((f) => f.priority === 'HIGH').length;

      // if item is sorted between / above the pinned items, mark it as pinned
      // otherwise it is below the pinned items, so unpin it
      movedItem.priority = newIndex < highCount ? 'HIGH' : 'LOW';

      sorted.splice(newIndex, 0, movedItem);

      // Build payloads with orders 1..N (no mutation of shared objects)
      const templateId = this.selectedTemplate.id;
      const updates = sorted.map((f, index) => ({
        fieldId: f.id,
        fieldData: { ...f, order: index + 1 },
      }));

      this.isUpdatingOrders = true;
      try {
        for (const { fieldId, fieldData } of updates) {
          await this.editEvaluationGridTemplateField({ templateId, fieldId, fieldData });
        }
        if (this.error) {
          this.$toast.error(this.error);
          return;
        }
        await this.onRefresh();
        this.$toast.success(this.$t('page.admin-evaluation-grid.toast.update-field-order-success'));
      } catch (error) {
        this.$toast.error(error);
      } finally {
        this.isUpdatingOrders = false;
      }
    },
    async onRefresh() {
      await this.getEvaluationGridTemplates();
      if (this.selectedTemplate && this.selectedTemplate.id) {
        const updatedTemplate = this.evaluationGridTemplates.find(
          (template) => template.id === this.selectedTemplate.id,
        );
        if (updatedTemplate) {
          this.selectedTemplate = { ...updatedTemplate };
        } else {
          this.selectedTemplate = this.evaluationGridTemplates[this.evaluationGridTemplates.length - 1];
        }
      } else {
        this.selectedTemplate =
          this.evaluationGridTemplates.length > 0
            ? this.evaluationGridTemplates[this.evaluationGridTemplates.length - 1]
            : null;
      }
    },
    getPinIcon(field) {
      return field.priority === 'LOW' ? 'pin-outline' : this.pinHovered === field.id ? 'pin-crossed' : 'pin';
    },
    getPinTooltip(field) {
      return field.priority === 'LOW' ? this.$t('documents-summary.pin') : this.$t('documents-summary.unpin');
    },
  },

  async created() {
    await this.getEvaluationGridTemplates();
    if (this.evaluationGridTemplates && this.evaluationGridTemplates.length > 0) {
      this.selectedTemplate = this.evaluationGridTemplates[0];
    }
  },
};
</script>

<style lang="scss">
.page-admin-evaluation-grid {
  padding: 32px;

  &__empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 100%;
    text-align: center;
    gap: 16px;

    &-message {
      font-size: $font-size-xbigger;
    }

    &-image {
      width: 240px;
      aspect-ratio: 1;
      display: block;
    }
  }

  &__actions {
    display: flex;
    align-items: center;
    gap: 16px;
  }

  &__template-actions {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  &__delete-icon {
    .base-icon {
      color: $color-error;
    }
  }

  &__table-wrapper {
    position: relative;

    tbody {
      cursor: default;
    }
  }

  &__table-header-actions {
    padding-bottom: 16px;
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  &__actions-menu {
    min-width: 140px;
    cursor: pointer;

    ul {
      padding: 8px 0;
    }
    li {
      padding: 8px 16px;
      list-style: none;
      align-items: center;
      display: flex;
      gap: 8px;

      .base-icon {
        width: 16px;
        height: 16px;
      }

      &:hover {
        background: $color-hover;
      }

      &.delete {
        color: $color-error;
      }
    }
  }

  &__more {
    cursor: pointer;
  }

  &__field-name {
    display: flex;
    gap: 4px;
    align-items: center;
  }

  &__item-choices {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
  }

  .v-select__slot {
    background: $color-white;
    padding-inline: 16px;
  }

  &__field-to-delete {
    font-weight: bolder;
    font-size: 16px;
    margin-top: 16px;
  }

  .v-input__slot {
    padding: 0 !important;
  }
  // Remove message and persistent hint
  .v-text-field__details {
    display: none;
  }
}
.v-menu__content:has(.page-admin-evaluation-grid__actions-menu) {
  box-shadow: $shadow-dropdown !important;
  border-radius: 5px;
  background: $color-white;
}
.page-admin-evaluation-grid .drag-handle {
  cursor: grab;
}
.page-admin-evaluation-grid__dnd-icon {
  width: 18px;
  margin-left: 8px;
}
.base-dialog__before-delete-dialog {
  height: 500px;
  width: 750px;
  position: relative;

  .base-dialog__header {
    padding-top: 24px;
  }
  .base-dialog__content {
    padding: 16px 24px 24px 24px !important;
    font-size: 18px;
    background: $color-white !important;
  }

  .close-icon__container {
    display: none;
  }
}
.before-delete-dialog {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 16px;
  height: 100%;

  .option-title {
    font-weight: 600;
    font-size: $font-size-root;
  }
  .option-dropdown {
    margin: 12px 0;
    height: 42px;

    .v-input__slot {
      margin-bottom: 0 !important;
    }

    .v-text-field__details {
      display: none !important;
    }
  }

  &__actions {
    margin-top: 32px;
    justify-content: flex-end;
    display: flex;
    gap: 12px;
  }
}
</style>
